import React, { useState, useMemo } from "react";
import {
    Table, TableHead, TableBody, TableRow, TableCell,
    Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    Box, TextField, InputAdornment, Typography, Chip, TablePagination,
    TableSortLabel
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { Search, PictureAsPdf } from "@mui/icons-material";
import GavelIcon from "@mui/icons-material/Gavel";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import GroupsIcon from "@mui/icons-material/Groups";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import InfoIcon from "@mui/icons-material/Info";
import DescriptionIcon from "@mui/icons-material/Description";
import SendIcon from "@mui/icons-material/Send";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonIcon from "@mui/icons-material/Person";
import EventIcon from "@mui/icons-material/Event";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import { format, isValid } from "date-fns";
import { es } from "date-fns/locale";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoSIGIF from "../../assets/sigifblue.png";

// ---------- estilos ----------
const useStyles = makeStyles(() => ({
    headerBar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 12,
        flexWrap: "wrap",
        gap: 8
    },
    searchInput: {
        width: 360,
        maxWidth: "100%"
    }
}));

// Función utilitaria para convertir una imagen a base64
export const getBase64FromImage = (imgPath, callback) => {
    const img = new window.Image();
    img.crossOrigin = "Anonymous";
    img.src = imgPath;
    img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL("image/png");
        callback(dataURL);
    };
};

const columnas = [
    { id: "fecha", label: "Fecha" },
    { id: "usuario", label: "Usuario" },
    { id: "accion", label: "Acción" },
    { id: "detalle", label: "Detalle" },
];

// ====== CATEGORÍAS Y COLORES DE ACCIONES =======
const CATEGORIAS = {
    MOVIMIENTO:  { label: "Movimientos",   color: "#5d7b9d", icon: GavelIcon },
    CERTIFICADO: { label: "Certificados",  color: "#8e6bbf", icon: ReceiptIcon },
    RADICACION:  { label: "Radicación",    color: "#e67e22", icon: AccountBalanceIcon },
    MEDIACION:   { label: "Mediación",     color: "#16a085", icon: GroupsIcon },
    VINCULADO:   { label: "Vinculados",    color: "#2980b9", icon: AttachFileIcon },
    EXPEDIENTE:  { label: "Expediente",    color: "#34495e", icon: DescriptionIcon },
    PASE:        { label: "Pases",         color: "#0288d1", icon: SendIcon },
    RESOLUCION:  { label: "Resoluciones",  color: "#6a1b9a", icon: AssignmentIcon },
    OTRO:        { label: "Otros",         color: "#7f8c8d", icon: InfoIcon },
};

// Tipo de operación → color e ícono
const OPERACION_COLORS = {
    CREAR:    { color: "#2e7d32", bg: "#e8f5e9", icon: AddCircleOutlineIcon },
    EDITAR:   { color: "#1565c0", bg: "#e3f2fd", icon: EditIcon },
    ELIMINAR: { color: "#c62828", bg: "#ffebee", icon: DeleteIcon },
    CAMBIAR:  { color: "#e65100", bg: "#fff3e0", icon: SwapHorizIcon },
    ASIGNAR:  { color: "#2e7d32", bg: "#e8f5e9", icon: AddCircleOutlineIcon },
    REACTIVACION: { color: "#00695c", bg: "#e0f2f1", icon: SwapHorizIcon },
    ENVIAR:   { color: "#0288d1", bg: "#e1f5fe", icon: SendIcon },
    ACEPTAR:  { color: "#2e7d32", bg: "#e8f5e9", icon: AddCircleOutlineIcon },
    RECHAZAR: { color: "#c62828", bg: "#ffebee", icon: DeleteIcon },
};

function parseAccion(accion) {
    if (!accion) return { categoria: "OTRO", operacion: null, subLabel: accion || "" };
    const upper = accion.toUpperCase().trim();

    // Formato nuevo: "CATEGORIA - OPERACION [DETALLE]"
    // Ej: "MOVIMIENTO - CREAR", "CERTIFICADOS - EDITAR", "RADICACION - CAMBIAR JUZGADO"
    const dashMatch = upper.match(/^([\wÁÉÍÓÚÑ]+?)S?\s*-\s*(.+)$/);
    if (dashMatch) {
        let cat = dashMatch[1];
        const resto = dashMatch[2].trim();

        // Normalizar categoría
        if (cat === "CERTIFICADO" || cat === "CERTIFICADOS") cat = "CERTIFICADO";
        else if (cat === "MOVIMIENTO" || cat === "MOVIMIENTOS") cat = "MOVIMIENTO";
        else if (cat === "RADICACION") cat = "RADICACION";
        else if (cat === "MEDIACION") cat = "MEDIACION";
        else if (cat === "VINCULADO" || cat === "VINCULADOS") cat = "VINCULADO";
        else if (cat === "PASE") cat = "PASE";
        else if (cat === "RESOLUCION" || cat === "RESOLUCIÓN") cat = "RESOLUCION";

        // Extraer operación del resto
        const operacionMatch = resto.match(/^(CREAR|EDITAR|ELIMINAR|CAMBIAR|ASIGNAR|REACTIVACION|ENVIAR|ACEPTAR|RECHAZAR)\b\s*(.*)?$/);
        if (operacionMatch) {
            return {
                categoria: CATEGORIAS[cat] ? cat : "OTRO",
                operacion: operacionMatch[1],
                subLabel: operacionMatch[2] || ""
            };
        }
        // Si no matchea operación conocida, usar el resto como sublabel
        return { categoria: CATEGORIAS[cat] ? cat : "OTRO", operacion: null, subLabel: resto };
    }

    // Formato viejo: "CREAR", "EDITAR", "ELIMINAR" (movimientos legacy)
    if (upper === "CREAR" || upper === "EDITAR" || upper === "ELIMINAR") {
        return { categoria: "MOVIMIENTO", operacion: upper, subLabel: "" };
    }

    // Formato legacy con espacio: "Crear Movimiento", "Actualización directa", etc.
    const spaceMatch = upper.match(/^(CREAR|EDITAR|ELIMINAR)\s+(MOVIMIENTO|CERTIFICADO|VINCULADO)/);
    if (spaceMatch) {
        let cat = spaceMatch[2];
        if (cat === "CERTIFICADO" || cat === "CERTIFICADOS") cat = "CERTIFICADO";
        return { categoria: CATEGORIAS[cat] ? cat : "MOVIMIENTO", operacion: spaceMatch[1], subLabel: "" };
    }

    return { categoria: "OTRO", operacion: null, subLabel: accion };
}

function getAccionDisplay(accion, detalle = "") {
    const parsed = parseAccion(accion);
    const catData = CATEGORIAS[parsed.categoria] || CATEGORIAS.OTRO;
    const opData = parsed.operacion ? (OPERACION_COLORS[parsed.operacion] || {}) : {};

    // Detectar acciones sobre expediente en formato viejo
    if (parsed.categoria === "MOVIMIENTO" && parsed.operacion) {
        const det = (detalle || "").toLowerCase();
        if (det.includes("expediente creado") || det.includes("expediente nuevo") || det.includes("expediente")) {
            if (parsed.operacion === "CREAR" && det.includes("expediente")) {
                // Solo si realmente es sobre expediente
            }
        }
    }

    // Construir label legible
    let label = catData.label;
    if (parsed.operacion) {
        const opLabels = {
            CREAR: "Crear", EDITAR: "Editar", ELIMINAR: "Eliminar",
            CAMBIAR: "Cambiar", ASIGNAR: "Asignar", REACTIVACION: "Reactivación"
        };
        label += " - " + (opLabels[parsed.operacion] || parsed.operacion);
    }
    if (parsed.subLabel) {
        const sub = parsed.subLabel.charAt(0).toUpperCase() + parsed.subLabel.slice(1).toLowerCase();
        label += " " + sub;
    }

    return {
        label,
        color: opData.color || catData.color,
        bg: opData.bg || "#f5f5f5",
        icon: opData.icon || catData.icon,
        catIcon: catData.icon,
        categoria: parsed.categoria,
        operacion: parsed.operacion
    };
}

// ====== FORMATEO DE FECHA FLEXIBLE ======
function formatFechaHistorial(fecha) {
    if (!fecha) return "";
    let dateObj;
    if (fecha.includes("-") && fecha.includes("T")) {
        dateObj = new Date(fecha);
    } else if (/^\d{1,2}\/\d{1,2}\/\d{4} \d{2}:\d{2}:\d{2}$/.test(fecha)) {
        const [d, m, yAndTime] = fecha.split("/");
        const [y, h, min, s] = yAndTime.split(/ |:|-/);
        dateObj = new Date(`${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}T${h}:${min}:${s}-03:00`);
    } else {
        dateObj = new Date(fecha);
    }
    if (!isValid(dateObj)) return fecha;
    return format(dateObj, "dd/MM/yyyy HH:mm:ss", { locale: es });
}

// ====== PARSEO DETALLE ======
function tryParseDetalle(detalle) {
    try {
        const obj = JSON.parse(detalle);
        if (obj && obj.tipo && obj.descripcion) return obj;
        return null;
    } catch {
        return null;
    }
}

// Parsear detalle pipe-separated en campos
function parseDetalleCampos(detalle) {
    if (!detalle || typeof detalle !== "string") return null;
    const limpio = detalle.replace(/\r?\n/g, " ").trim();

    // Intentar JSON primero
    if (limpio.startsWith("{")) {
        try {
            const obj = JSON.parse(limpio);
            const campos = [];
            if (obj.tipo) campos.push({ key: "Tipo", value: obj.tipo });
            if (obj.descripcion) campos.push({ key: "Descripción", value: obj.descripcion });
            if (obj.fecha) campos.push({ key: "Fecha", value: formatFechaHistorial(obj.fecha) });
            if (obj.responsable) campos.push({ key: "Responsable", value: obj.responsable });
            return campos.length > 0 ? campos : null;
        } catch { /* no es JSON */ }
    }

    // Formato pipe: "Tipo='X' | Descripción='Y' | Fecha='Z' | Responsable='W'"
    // o con flechas: "Tipo: 'Antes' → 'Después' | Descripción: 'X' → 'Y'"
    if (limpio.includes("|")) {
        const partes = limpio.split(/\s*\|\s*/);
        // Verificar que la mayoría de las partes sean campos válidos (clave=valor o clave: valor)
        const esCampoValor = partes.filter(p =>
            /^[\wÁÉÍÓÚÜÑáéíóúüñ\s]+?(\s*=\s*|:\s*)/.test(p.trim())
        ).length >= Math.ceil(partes.length / 2);

        if (esCampoValor) {
            const campos = [];
            for (const p of partes) {
                // Formato "Campo='Valor'"
                const matchEq = p.match(/^([\wÁÉÍÓÚÜÑáéíóúüñ\s]+?)\s*=\s*'?(.+?)'?\s*$/);
                if (matchEq) {
                    campos.push({ key: matchEq[1].trim(), value: matchEq[2].trim() });
                    continue;
                }
                // Formato "Campo: 'Antes' → 'Despues'"
                const matchArrow = p.match(/^([\wÁÉÍÓÚÜÑáéíóúüñ\s]+?):\s*'?(.+?)'?\s*→\s*'?(.+?)'?\s*$/);
                if (matchArrow) {
                    campos.push({ key: matchArrow[1].trim(), antes: matchArrow[2].trim(), despues: matchArrow[3].trim() });
                    continue;
                }
                // Formato "Campo: Valor"
                const matchColon = p.match(/^([\wÁÉÍÓÚÜÑáéíóúüñ\s]+?):\s*(.+)$/);
                if (matchColon) {
                    campos.push({ key: matchColon[1].trim(), value: matchColon[2].trim() });
                    continue;
                }
                // Si no matchea nada, agregar como texto
                if (p.trim()) campos.push({ key: null, value: p.trim() });
            }
            return campos.length > 0 ? campos : null;
        }
    }

    // Formato simple "Campo: Valor → Nuevo" (sin pipes)
    const matchArrowSimple = limpio.match(/^([\wÁÉÍÓÚÜÑáéíóúüñ\s]+?):\s*'?(.+?)'?\s*→\s*'?(.+?)'?\s*$/);
    if (matchArrowSimple) {
        return [{ key: matchArrowSimple[1].trim(), antes: matchArrowSimple[2].trim(), despues: matchArrowSimple[3].trim() }];
    }

    return null;
}

// ====== LIMPIAR DETALLE PARA PDF ======
// Nota: jsPDF helvetica no soporta caracteres Unicode como → ñ á etc.
// Reemplazar al final para que el PDF los renderice bien.
function sanitizePDF(text) {
    return text
        .replace(/→/g, '->')
        .replace(/←/g, '<-')
        .replace(/[""]/g, '"')
        .replace(/['']/g, "'");
}

function limpiarDetalleParaPDF(detalle) {
    if (!detalle) return "";
    let limpio = detalle.replace(/\r?\n/g, " ").replace(/\s+/g, " ").trim();

    // Cambios con flecha: "Tipo: 'Antes' → 'Después' | Descripción: 'X' → 'Y'"
    if (limpio.includes("→")) {
        const partes = limpio.split(/\s*\|\s*/);
        // Verificar que cada parte sea campo: valor → valor
        const esCampoFlecha = partes.filter(p =>
            /^[\wÁÉÍÓÚÜÑáéíóúüñ\s]+?:\s*.+→/.test(p.trim())
        ).length >= Math.ceil(partes.length / 2);

        if (esCampoFlecha) {
            const result = partes.map(p => {
                const reg = /^([\wÁÉÍÓÚÜÑáéíóúüñ\s]+?):\s*'?(.+?)'?\s*→\s*'?(.+?)'?\s*$/;
                const m = p.match(reg);
                if (m) return `${m[1].trim()}: "${m[2]}" -> "${m[3]}"`;
                return p;
            });
            return sanitizePDF(result.join('\n'));
        }
        // Si no es campo:flecha, devolver todo en una línea
        return sanitizePDF(limpio);
    }

    // Formato pipe con =: "Tipo='X' | Descripción='Y'"
    if (limpio.includes("|") && limpio.includes("=")) {
        const partes = limpio.split(/\s*\|\s*/);
        const esCampoValor = partes.filter(p =>
            /^[\wÁÉÍÓÚÜÑáéíóúüñ\s]+?\s*=/.test(p.trim())
        ).length >= Math.ceil(partes.length / 2);

        if (esCampoValor) {
            const result = partes.map(p => {
                const m = p.match(/^([\wÁÉÍÓÚÜÑáéíóúüñ\s]+?)='?(.+?)'?\s*$/);
                if (m) return `${m[1].trim()}: ${m[2]}`;
                return p;
            });
            return sanitizePDF(result.join('\n'));
        }
    }

    // Formato pipe con ": Tipo: X | Descripción: Y" (solo si CADA parte es clave:valor)
    if (limpio.includes("|") && limpio.includes(":")) {
        const partes = limpio.split(/\s*\|\s*/);
        const esCampoValor = partes.every(p => /^[\wÁÉÍÓÚÜÑáéíóúüñ\s]+:/.test(p.trim()));
        if (esCampoValor) {
            return sanitizePDF(partes.join('\n'));
        }
    }

    // JSON parse
    if (typeof detalle === "string" && limpio.startsWith("{")) {
        try {
            const obj = JSON.parse(limpio);
            let campos = [];
            if (obj.tipo) campos.push(`Tipo: ${obj.tipo}`);
            if (obj.descripcion) campos.push(`Descripcion: ${obj.descripcion}`);
            if (obj.fecha) campos.push(`Fecha: ${formatFechaHistorial(obj.fecha)}`);
            if (obj.responsable) campos.push(`Responsable: ${obj.responsable}`);
            return sanitizePDF(campos.join("\n"));
        } catch { return sanitizePDF(limpio); }
    }

    return sanitizePDF(limpio);
}


export default function HistorialCambios({ historial = [], expedienteInfo }) {
    const classes = useStyles();
    const [detalleDialog, setDetalleDialog] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [search, setSearch] = useState("");
    const [orderBy, setOrderBy] = useState("fecha");
    const [order, setOrder] = useState("desc");
    const [filtroCategoria, setFiltroCategoria] = useState(null); // null = todas

    // Contar por categoría para los chips
    const conteosCat = useMemo(() => {
        const counts = {};
        const filtered = historial.filter(row =>
            row.accion !== "Actualización directa" && row.detalle !== "Update desde trigger"
        );
        filtered.forEach(row => {
            const { categoria } = parseAccion(row.accion);
            counts[categoria] = (counts[categoria] || 0) + 1;
        });
        return counts;
    }, [historial]);

    // Categorías presentes (solo mostrar las que tienen datos)
    const categoriasPresentes = useMemo(() => {
        return Object.keys(CATEGORIAS).filter(k => conteosCat[k] > 0);
    }, [conteosCat]);

    // FILTRADO Y ORDEN
    const historialFiltrado = useMemo(() => {
        let arr = historial.filter(row =>
            row.accion !== "Actualización directa" &&
            row.detalle !== "Update desde trigger"
        );
        // Filtro por categoría
        if (filtroCategoria) {
            arr = arr.filter(row => {
                const { categoria } = parseAccion(row.accion);
                return categoria === filtroCategoria;
            });
        }
        if (search) {
            const q = search.toLowerCase();
            arr = arr.filter(row =>
                (row.usuario && row.usuario.toLowerCase().includes(q)) ||
                (row.accion && row.accion.toLowerCase().includes(q)) ||
                (row.detalle && row.detalle.toLowerCase().includes(q))
            );
        }
        arr = arr.slice().sort((a, b) => {
            let vA = a[orderBy], vB = b[orderBy];
            if (orderBy === "fecha") {
                vA = new Date(a.fecha);
                vB = new Date(b.fecha);
            } else {
                vA = (vA || "").toString().toLowerCase();
                vB = (vB || "").toString().toLowerCase();
            }
            if (vA < vB) return order === "asc" ? -1 : 1;
            if (vA > vB) return order === "asc" ? 1 : -1;
            return 0;
        });
        return arr;
    }, [historial, search, orderBy, order, filtroCategoria]);

    const rowsToShow = historialFiltrado.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    function handleOrder(col) {
        if (orderBy === col) setOrder(order === "asc" ? "desc" : "asc");
        else {
            setOrderBy(col);
            setOrder(col === "fecha" ? "desc" : "asc");
        }
    }

    // Reset page on filter/search change
    React.useEffect(() => { setPage(0); }, [search, orderBy, order, filtroCategoria]);

    async function exportToPDF() {
        let tipoExp = expedienteInfo?.tipo === "judicial" ? "Judicial" : "Administrativo";
        let campos = [];

        if (tipoExp === "Judicial") {
            campos = [
                { label: "Autos:", value: expedienteInfo?.autos_1_instancia || expedienteInfo?.nro_expediente || "-" },
                { label: "Carátula:", value: expedienteInfo?.caratula || "-" },
                { label: "Tipo Proceso:", value: expedienteInfo?.tipo_proceso || "-" }
            ];
        } else {
            campos = [
                { label: "Nro Expediente:", value: expedienteInfo?.nro_expediente || "-" },
                { label: "Iniciador:", value: expedienteInfo?.iniciador || "-" },
                { label: "Extracto:", value: expedienteInfo?.extracto || "-" }
            ];
        }

        const estado = expedienteInfo?.estado || expedienteInfo?.estado_proceso || "-";

        getBase64FromImage(logoSIGIF, (logoBase64) => {
            const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(23, 30, 60);
            doc.text("Fiscalía de Estado - San Juan", 40, 60);

            doc.setFontSize(12);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(31, 75, 130);
            doc.text(`Expediente ${tipoExp} - Historial de Cambios`, 40, 80);

            doc.addImage(logoBase64, "PNG", doc.internal.pageSize.getWidth() - 175, 28, 150, 65);

            let startY = 110;
            const lineSpacing = 20;

            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(23, 105, 170);
            campos.forEach((campo, idx) => { doc.text(campo.label, 40, startY + idx * lineSpacing); });

            doc.setFont("helvetica", "normal");
            doc.setTextColor(24, 52, 96);
            campos.forEach((campo, idx) => {
                const texto = String(campo.value).replace(/\s+/g, ' ').trim();
                doc.text(texto, 170, startY + idx * lineSpacing);
            });

            doc.text("Estado:", 40, startY + campos.length * lineSpacing);
            doc.setFillColor(23, 105, 170);
            doc.roundedRect(170, startY + campos.length * lineSpacing - 13, 330, 27, 10, 10, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.setTextColor(255, 255, 255);
            doc.text(estado, 185, startY + campos.length * lineSpacing + 6);

            doc.setFontSize(12);
            doc.setTextColor(0);

            // Pre-calcular datos de acción para colorear en el PDF
            const accionDataPorFila = historialFiltrado.map(row => getAccionDisplay(row.accion, row.detalle));

            autoTable(doc, {
                startY: startY + campos.length * lineSpacing + 40,
                head: [columnas.map(c => c.label)],
                body: historialFiltrado.map(row => [
                    formatFechaHistorial(row.fecha),
                    row.usuario,
                    getAccionDisplay(row.accion, row.detalle).label,
                    limpiarDetalleParaPDF(row.detalle)
                ]),
                styles: {
                    font: "helvetica", fontSize: 9, cellPadding: 3,
                    overflow: 'linebreak', lineColor: [200, 200, 200], lineWidth: 0.5
                },
                columnStyles: {
                    0: { cellWidth: 75 }, 1: { cellWidth: 65 },
                    2: { cellWidth: 95, fontStyle: 'bold' }, 3: { cellWidth: 305 }
                },
                headStyles: { fillColor: [23, 105, 170], textColor: 255, fontSize: 10, fontStyle: 'bold' },
                bodyStyles: { textColor: 30, fontSize: 8 },
                alternateRowStyles: { fillColor: [245, 245, 245] },
                margin: { left: 30, right: 30 },
                // Colorear la columna Acción y el fondo de fila
                didParseCell: (data) => {
                    if (data.section === 'body' && data.row.index < accionDataPorFila.length) {
                        const ad = accionDataPorFila[data.row.index];
                        // Colorear celda de acción (columna 2)
                        if (data.column.index === 2) {
                            const hex = ad.color || '#555555';
                            const r = parseInt(hex.slice(1, 3), 16);
                            const g = parseInt(hex.slice(3, 5), 16);
                            const b = parseInt(hex.slice(5, 7), 16);
                            data.cell.styles.textColor = [r, g, b];
                            data.cell.styles.fontStyle = 'bold';
                        }
                        // Fondo suave de fila según operación
                        if (ad.bg && ad.bg !== '#f5f5f5') {
                            const hex = ad.bg;
                            const r = parseInt(hex.slice(1, 3), 16);
                            const g = parseInt(hex.slice(3, 5), 16);
                            const b = parseInt(hex.slice(5, 7), 16);
                            data.cell.styles.fillColor = [r, g, b];
                        }
                    }
                }
            });

            const pages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pages; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(150);
                doc.text(
                    `Generado el ${new Date().toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })} - Página ${i} de ${pages}`,
                    40, doc.internal.pageSize.getHeight() - 20
                );
            }

            const pdfUrl = doc.output('bloburl');
            window.open(pdfUrl, '_blank');
        });
    }

    // Renderizar detalle estructurado
    function renderDetalle(row) {
        const detalleParsed = tryParseDetalle(row.detalle);
        if (detalleParsed) {
            return (
                <Button variant="outlined" size="small" color="primary"
                    style={{ fontWeight: 600, borderRadius: 4, textTransform: "none" }}
                    onClick={() => setDetalleDialog(detalleParsed)}>
                    Ver detalles
                </Button>
            );
        }

        const campos = parseDetalleCampos(row.detalle);
        if (campos) {
            return (
                <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
                    {campos.map((c, i) => {
                        if (c.antes && c.despues) {
                            // Cambio: antes → después
                            return (
                                <Chip key={i} size="small" variant="outlined"
                                    label={<span>{c.key}: <span style={{ textDecoration: "line-through", opacity: 0.6 }}>{c.antes}</span> → <b>{c.despues}</b></span>}
                                    style={{ height: 'auto', padding: '2px 0' }}
                                />
                            );
                        }
                        if (c.key) {
                            return <Chip key={i} size="small" variant="filled"
                                label={`${c.key}: ${c.value}`}
                                style={{ background: "#f0f4f8", fontSize: 12 }}
                            />;
                        }
                        return <Typography key={i} variant="body2" style={{ fontSize: 13 }}>{c.value}</Typography>;
                    })}
                </Box>
            );
        }

        // Texto plano
        return <span style={{ whiteSpace: "pre-line", fontSize: 13 }}>{row.detalle}</span>;
    }

    return (
        <Paper style={{ marginTop: 16, padding: 16, borderRadius: 4 }}>
            {/* Header: búsqueda + PDF */}
            <Box className={classes.headerBar}>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <TextField
                        className={classes.searchInput}
                        variant="outlined"
                        size="small"
                        placeholder="Buscar usuario, acción o detalle..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search style={{ opacity: 0.6 }} />
                                </InputAdornment>
                            ),
                            style: { borderRadius: 4 }
                        }}
                    />
                    {search && (
                        <Chip
                            label={`${historialFiltrado.length} resultado${historialFiltrado.length !== 1 ? 's' : ''}`}
                            size="small" color="primary" style={{ fontWeight: 600 }}
                        />
                    )}
                </Box>

                <Button
                    onClick={exportToPDF}
                    startIcon={<PictureAsPdf />}
                    variant="contained"
                    style={{
                        borderRadius: 4, background: "#1769aa", fontWeight: 600,
                        textTransform: "none", color: "#fff"
                    }}
                >
                    Exportar PDF
                </Button>
            </Box>

            {/* Chips de filtro por categoría */}
            {categoriasPresentes.length > 1 && (
                <Box display="flex" gap={1} mb={2} flexWrap="wrap" alignItems="center">
                    <Chip
                        label={`Todos (${historial.filter(r => r.accion !== "Actualización directa" && r.detalle !== "Update desde trigger").length})`}
                        size="small"
                        variant={filtroCategoria === null ? "filled" : "outlined"}
                        color={filtroCategoria === null ? "primary" : "default"}
                        onClick={() => setFiltroCategoria(null)}
                        style={{ fontWeight: 600, cursor: "pointer" }}
                    />
                    {categoriasPresentes.map(catKey => {
                        const catData = CATEGORIAS[catKey];
                        const CatIcon = catData.icon;
                        const isActive = filtroCategoria === catKey;
                        return (
                            <Chip
                                key={catKey}
                                icon={<CatIcon style={{ fontSize: 16, color: isActive ? "#fff" : catData.color }} />}
                                label={`${catData.label} (${conteosCat[catKey]})`}
                                size="small"
                                variant={isActive ? "filled" : "outlined"}
                                onClick={() => setFiltroCategoria(isActive ? null : catKey)}
                                style={{
                                    fontWeight: 600, cursor: "pointer",
                                    background: isActive ? catData.color : "transparent",
                                    color: isActive ? "#fff" : catData.color,
                                    borderColor: catData.color
                                }}
                            />
                        );
                    })}
                </Box>
            )}

            {/* Tabla */}
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {columnas.map(col => (
                            <TableCell key={col.id} style={{ fontWeight: 700 }}
                                sortDirection={orderBy === col.id ? order : false}>
                                <TableSortLabel
                                    active={orderBy === col.id}
                                    direction={orderBy === col.id ? order : "asc"}
                                    onClick={() => handleOrder(col.id)}
                                >
                                    {col.label}
                                </TableSortLabel>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rowsToShow.map((row, idx) => {
                        const accionData = getAccionDisplay(row.accion, row.detalle);
                        // eslint-disable-next-line no-unused-vars
                        const OpIcon = accionData.icon;
                        const CatIcon = accionData.catIcon;
                        return (
                            <TableRow key={idx} hover style={{ background: accionData.bg }}>
                                <TableCell style={{ fontSize: 13, whiteSpace: "nowrap" }}>
                                    {formatFechaHistorial(row.fecha)}
                                </TableCell>
                                <TableCell style={{ fontSize: 13 }}>{row.usuario}</TableCell>
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <CatIcon style={{ fontSize: 16, color: accionData.color, opacity: 0.7 }} />
                                        <span style={{ color: accionData.color, fontWeight: 700, fontSize: 13 }}>
                                            {accionData.label}
                                        </span>
                                    </Box>
                                </TableCell>
                                <TableCell>{renderDetalle(row)}</TableCell>
                            </TableRow>
                        );
                    })}
                    {rowsToShow.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={4} align="center">
                                <Typography color="text.secondary" style={{ padding: 20 }}>
                                    {search || filtroCategoria ? "No se encontraron resultados con los filtros aplicados." : "No hay historial de cambios."}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            {/* Paginación MUI */}
            <TablePagination
                component="div"
                count={historialFiltrado.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 15, 25, 50]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />

            {/* Dialogo de detalles (JSON movimiento) */}
            <Dialog open={!!detalleDialog} onClose={() => setDetalleDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle style={{
                    background: "#5d7b9d", color: "#fff", fontWeight: 700,
                    padding: "14px 20px"
                }}>
                    Detalles del movimiento
                </DialogTitle>
                <DialogContent style={{ padding: "20px 24px 8px 24px" }}>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <BookmarkIcon color="action" />
                            <Typography><b>Tipo:</b> {detalleDialog?.tipo || <span style={{ color: "#bbb" }}>Sin tipo</span>}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            <DescriptionIcon color="action" />
                            <Typography><b>Descripción:</b> {detalleDialog?.descripcion || <span style={{ color: "#bbb" }}>Sin descripción</span>}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            <EventIcon color="action" />
                            <Typography><b>Fecha:</b> {detalleDialog?.fecha
                                ? (() => {
                                    let fecha = new Date(detalleDialog.fecha);
                                    if (typeof detalleDialog.fecha === "string" && detalleDialog.fecha.length === 10) {
                                        fecha = new Date(`${detalleDialog.fecha}T00:00:00-03:00`);
                                    }
                                    return new Intl.DateTimeFormat("es-AR", {
                                        day: "2-digit", month: "2-digit", year: "numeric",
                                        hour: "2-digit", minute: "2-digit", second: "2-digit",
                                        hour12: false, timeZone: "America/Argentina/Buenos_Aires"
                                    }).format(fecha);
                                })()
                                : <span style={{ color: "#bbb" }}>Sin fecha</span>
                            }</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon color="action" />
                            <Typography><b>Responsable:</b> {detalleDialog?.responsable || <span style={{ color: "#bbb" }}>Sin responsable</span>}</Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions style={{ padding: "12px 24px 16px" }}>
                    <Button onClick={() => setDetalleDialog(null)} color="primary" variant="contained"
                        style={{ fontWeight: 600, textTransform: "none" }}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
